import React, { useState } from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { Button, Input, Modal, Stack, TextArea } from "@libertify/ui";

interface RenamePlaybookModalProps {
  open: boolean;
  /** The playbook's current name, pre-filled for editing. */
  currentName: string;
  /** Its current description, if it has one. */
  currentDescription?: string;
  /** The rename is in flight — the modal stays open until it resolves. */
  isSaving?: boolean;
  onCancel: () => void;
  onConfirm: (name: string, description: string | null) => void;
}

/**
 * Edits the name and description of one of the organization's playbooks.
 *
 * Its add-on configuration is edited in the customize view instead, so those
 * two fields are all there is.
 */
export const RenamePlaybookModal: React.FC<RenamePlaybookModalProps> = ({
  open,
  currentName,
  currentDescription,
  isSaving = false,
  onCancel,
  onConfirm,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const [name, setName] = useState(currentName);
  const [description, setDescription] = useState(currentDescription ?? "");

  // Reset on the closed→open transition, adjusted during render (React's
  // documented pattern) rather than in an effect, so the fields never paint
  // one frame of the previous session's values.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(currentName);
      setDescription(currentDescription ?? "");
    }
  }

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !isSaving;

  const save = () => {
    if (!canSave) return;
    // Emptied on purpose means clear it, so `null` rather than `undefined` —
    // the latter would leave the old description in place.
    onConfirm(trimmed, description.trim() || null);
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="medium"
      title={t("createExperience.renamePlaybookModal.title")}
      styles={{
        header: { display: "flex", alignItems: "center" },
        footer: { background: token.colorBgContainer },
      }}
      footer={
        <Stack fullWidth justify="end" align="center" gap={12}>
          <Button variant="outlined" size="m" onClick={onCancel}>
            {t("createExperience.renamePlaybookModal.cancel")}
          </Button>
          <Button
            variant="primary"
            size="m"
            disabled={!canSave}
            loading={isSaving}
            onClick={save}
          >
            {t("createExperience.renamePlaybookModal.save")}
          </Button>
        </Stack>
      }
    >
      <Stack direction="vertical" gap={16} fullWidth>
        <Input
          autoFocus
          label={t("createExperience.renamePlaybookModal.nameLabel")}
          placeholder={t(
            "createExperience.renamePlaybookModal.namePlaceholder"
          )}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={save}
        />
        <TextArea
          rows={3}
          label={t("createExperience.renamePlaybookModal.descriptionLabel")}
          placeholder={t(
            "createExperience.renamePlaybookModal.descriptionPlaceholder"
          )}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Stack>
    </Modal>
  );
};
